<template>
  <!-- 整个表单使用el-form组件，ref用于表单验证，v-loading显示加载状态 -->
  <el-form
    ref="thirdRuleForm"
    v-loading="formLoading"
    :model="thirdStepRuleForm"
    :rules="thirdStepRules"
    class="thirdStep"
    label-position="left"
    label-width="160px"
  >
    <!-- 计费方式选择表单项 -->
    <el-form-item :label="$t('createNode.billingMethod')" prop="billingValue">
      <el-select
        v-model="thirdStepRuleForm.billingValue"
        :placeholder="$t('createNode.selectBillingMethod')"
        class="select-width"
        @change="
          (value) => {
            selectHandler(value, 'billingValue');
          }
        "
      >
        <!-- 遍历计费方式选项 -->
        <el-option v-for="item in billingTypeData" :key="item.value" :label="item.text" :value="item.value" />
      </el-select>
    </el-form-item>

    <!-- 地域选择表单项 -->
    <el-form-item :label="$t('createNode.region')" prop="regionValue">
      <el-select
        v-model="thirdStepRuleForm.regionValue"
        :disabled="!isCreateAdminNode && dialogType === 'createNode'"
        :placeholder="$t('createNode.selectMachineRegion')"
        class="select-width"
        @change="
          (value) => {
            selectHandler(value, 'region');
          }
        "
      >
        <!-- 遍历地域选项 -->
        <el-option v-for="item in regionData" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
    </el-form-item>

    <!-- 集群环境选择表单项（仅管理员节点可见） -->
    <el-form-item v-if="isCreateAdminNode" :label="$t('createNode.clusterEnv')" class="select-width" filterable>
      <el-select
        v-model="thirdStepRuleForm.envId"
        :placeholder="$t('createNode.selectClusterEnvInfo')"
        filterable
        clearable
        @change="
          (value) => {
            selectEnvId(value);
          }
        "
        @clear="clearEnv"
      >
        <!-- 遍历环境列表选项 -->
        <el-option v-for="item in envList" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
    </el-form-item>

    <!-- 子网信息表单项 -->
    <el-form-item :label="$t('createNode.subnetInfo')">
      <div class="base-flex">
        <!-- 地区选择 -->
        <el-form-item class="base-margin__right-10" prop="zone">
          <el-select
            v-model="thirdStepRuleForm.zone"
            :placeholder="$t('createNode.selectZone')"
            class="base-margin__right-10"
            filterable
            @change="changeVpcZone"
          >
            <el-option v-for="item in zones" :key="item.ZoneId" :label="item.ZoneName" :value="item.Zone" />
          </el-select>
        </el-form-item>
        <!-- 子网选择 -->
        <el-form-item prop="subnetId">
          <el-select v-model="thirdStepRuleForm.subnetId" :placeholder="$t('createNode.selectSubnetInfo')" filterable>
            <el-option
              v-for="item in subnetList"
              :key="item.SubnetId"
              :label="`${item.SubnetId} | ${item.SubnetName} | ${item.CidrBlock}`"
              :value="item.SubnetId"
            />
          </el-select>
        </el-form-item>
      </div>
    </el-form-item>

    <!-- 镜像选择表单项 -->
    <el-form-item :label="$t('createNode.image')" prop="imageId">
      <div class="base-width_fullscreen">
        <div class="base-flex base-margin__bottom-10">
          <el-select
            v-model="thirdStepRuleForm.imageType"
            :placeholder="$t('createNode.selectImageType')"
            class="formItem__select-short base-margin__right-20"
            @change="changeImageType('imageType')"
          >
            <el-option :label="$t('createNode.sharedImages')" value="SHARED_IMAGE"></el-option>
            <el-option :label="$t('createNode.privateImages')" value="PRIVATE_IMAGE"></el-option>
          </el-select>
          <el-select
            v-model="thirdStepRuleForm.imagePlatformType"
            :placeholder="$t('createNode.selectImageType')"
            class="formItem__select-short base-margin__right-20"
            @change="changeImageType('imagePlatform')"
          >
            <el-option
              v-for="item in imageTypeList"
              :key="item.value"
              :label="item.text"
              :value="item.value"
            ></el-option>
          </el-select>
        </div>
        <el-select
          v-model="thirdStepRuleForm.imageId"
          :placeholder="$t('createNode.selectImage')"
          class="select-width"
          filterable
          @change="
            (value) => {
              selectHandler(value, 'imageId');
            }
          "
        >
          <!-- 遍历镜像选项 -->
          <el-option
            v-for="item in imageOption"
            :key="item.value"
            :label="`${item.text} | ${item.value}`"
            :value="item.value"
          >
            <el-tooltip class="item" :open-delay="100" effect="dark" content="Top Center 提示文字" placement="bottom">
              <template slot="content">
                <span>
                  <p>镜像名称: {{ item.text }}</p>
                  <p>镜像ID: {{ item.value }}</p>
                  <p>系统盘容量: {{ item.size }}GiB</p>
                  <p>操作系统: {{ item.osName }}</p>
                </span>
              </template>
              <span>{{ item.text }} | {{ item.value }}</span>
            </el-tooltip>
          </el-option>
        </el-select>
      </div>
    </el-form-item>

    <!-- 子节点类型选择表单项（非管理员节点可见） -->
    <el-form-item v-if="!isCreateAdminNode" :label="$t('createNode.childNodeType')" prop="nodeType">
      <el-select
        v-model="thirdStepRuleForm.nodeType"
        :placeholder="$t('createNode.selectNodeType')"
        class="select-width"
        @change="
          (value) => {
            selectHandler(value, 'nodeType');
          }
        "
      >
        <!-- 遍历节点类型选项 -->
        <el-option
          v-for="item in nodeTypeList"
          :key="item.value"
          :disabled="item.disabled"
          :label="item.text"
          :value="item.value"
        />
      </el-select>
    </el-form-item>

    <!-- 机器型号表单项 -->
    <el-form-item :label="$t('createNode.machineModel')" prop="model">
      <el-row>
        <el-col :span="12">
          <!-- 服务器机型选择 -->
          <el-form-item class="form__item-margin base-margin__right-10" prop="model">
            <el-select
              v-model="thirdStepRuleForm.model"
              :placeholder="$t('createNode.selectServerModel')"
              filterable
              @change="
                (value) => {
                  selectHandler(value, 'type');
                }
              "
            >
              <!-- 遍历机型选项 -->
              <el-option
                v-for="item in instanceTypeOption"
                :key="item.instanceFamily"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <!-- 服务器配置选择 -->
          <el-form-item class="form__item-margin" prop="auditor">
            <el-select
              v-model="thirdStepRuleForm.auditor"
              :placeholder="$t('createNode.selectServerConfig')"
              filterable
              @change="
                (value) => {
                  selectHandler(value, 'description');
                }
              "
            >
              <!-- 遍历配置选项，带详细信息的tooltip -->
              <el-option
                v-for="item in instanceDescriptionOption"
                :key="item.value"
                :disabled="item.disabled"
                :label="item.text"
                :value="item.value"
              >
                <div class="base-flex__between base-flex__cross-center">
                  <span>{{ item.text }}</span>
                  <el-tooltip offset="20" placement="right" popper-class="tooltipBox">
                    <div slot="content">
                      <p>{{ $t('createNode.vCPU') }}: {{ item.vCpu || '-' }}{{ $t('createNode.core') }}</p>
                      <p>{{ $t('createNode.memory') }}: {{ item.memory || '-' }}GiB</p>
                      <p>{{ $t('createNode.processorFrequency') }}: {{ item.frequency || '-' }}</p>
                      <p>{{ $t('createNode.processorModel') }}: {{ item.cpuType || '-' }}</p>
                      <p>{{ $t('createNode.internalBandwidth') }}: {{ item.instanceBandwidth || '-' }}Gbps</p>
                      <p>
                        {{ $t('createNode.networkPackets') }}: {{ item.instancePps || '-'
                        }}{{ $t('createNode.tenThousandPPS') }}
                      </p>
                    </div>
                    <i class="el-icon-warning-outline base-text-align__right" />
                  </el-tooltip>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form-item>

    <!-- 调度器选择表单项（Windows类型可见） -->
    <el-form-item v-if="windowsType" :label="$t('createNode.scheduler')" prop="workloadManager">
      <el-select
        v-model="thirdStepRuleForm.workloadManager"
        :disabled="!isCreateAdminNode && dialogType === 'createNode'"
        :placeholder="$t('createNode.selectScheduler')"
        class="select-width"
        @change="changeDisk"
      >
        <!-- 遍历调度器选项 -->
        <el-option v-for="item in workloadManagerList" :key="item.value" :label="item.text" :value="item.value" />
      </el-select>
    </el-form-item>

    <!-- 系统盘表单项 -->
    <el-form-item :label="$t('createNode.systemDisk')" prop="diskType">
      <el-row>
        <el-col :span="12">
          <!-- 磁盘类型选择 -->
          <el-form-item class="form__item-margin base-margin__right-10">
            <el-select
              v-model="thirdStepRuleForm.diskType"
              :placeholder="$t('createNode.selectSystemDisk')"
              class="base-margin__right-20"
              prop="diskType"
              @change="changeDisk"
            >
              <el-option
                v-for="item in diskList"
                :key="item.key"
                :label="`${item.key} | ${item.remark}`"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <!-- 磁盘大小输入 -->
          <el-form-item class="form__item-margin">
            <el-input-number
              v-model="thirdStepRuleForm.diskSize"
              :min="gibMinNumber"
              prop="diskSize"
              style="width: 150px"
              @change="changeDiskSize"
            />
            GIB
          </el-form-item>
        </el-col>
      </el-row>
    </el-form-item>

    <!-- 队列表单项（非管理员节点可见） -->
    <el-form-item v-if="!isCreateAdminNode" :label="$t('createNode.queue')" prop="queueName">
      <el-autocomplete
        v-model="thirdStepRuleForm.queueName"
        :fetch-suggestions="autocompleteQuerySearch"
        :placeholder="$t('createNode.inputContent')"
        class="select-width"
      />
    </el-form-item>

    <!-- 购买时长表单项（包年包月计费方式可见） -->
    <el-form-item v-if="isPostpaid" :label="$t('createNode.purchaseDuration')" prop="buyTime">
      <el-select
        v-model="thirdStepRuleForm.buyTime"
        :placeholder="$t('createNode.selectPurchaseDuration')"
        class="select-width"
        @change="getPrice"
      >
        <!-- 遍历购买时长选项 -->
        <el-option v-for="item in buyTimeOptions" :key="item.value" :label="item.text" :value="item.value" />
      </el-select>
    </el-form-item>

    <!-- 续费方式表单项（包年包月计费方式可见） -->
    <el-form-item v-if="isPostpaid" :label="$t('createNode.renewalMethod')" prop="renew">
      <el-select
        v-model="thirdStepRuleForm.renew"
        :placeholder="$t('createNode.selectRenewalMethod')"
        class="select-width"
      >
        <!-- 遍历续费方式选项 -->
        <el-option v-for="item in renewOptions" :key="item.value" :label="item.text" :value="item.value" />
      </el-select>
    </el-form-item>

    <!-- 计算节点数量表单项（创建节点时可见） -->
    <el-form-item v-if="dialogType === 'createNode'" :label="$t('createNode.nodeCount')" prop="instanceNumber">
      <el-input-number
        v-model="thirdStepRuleForm.instanceNumber"
        :disabled="isCreateAdminNode"
        :max="99"
        :min="1"
        class="select-width_90"
      />
    </el-form-item>

    <!-- 登录方式表单项（Windows类型可见） -->
    <el-form-item v-if="windowsType" :label="$t('createNode.loginMethod')">
      <el-radio-group v-model="passwordRadio" @input="checkPasswordRadio">
        <el-radio :label="false">{{ $t('createNode.setPassword') }}</el-radio>
        <el-radio :label="true">{{ $t('createNode.associateKeyImmediately') }}</el-radio>
      </el-radio-group>

      <!-- 密码设置区域 -->
      <div v-if="!displayPasswordType">
        <div class="base-margin__bottom-20">
          <span class="base-margin__right-32" style="margin-right: 20px">
            {{ $t('createNode.password') }}
            <el-popover placement="top-start" trigger="hover" width="330">
              <div class="tips">
                <p>{{ $t('createNode.passwordTip1') }}</p>
                <p>{{ $t('createNode.passwordTip2') }}</p>
                <p>{{ $t('createNode.passwordTip3') }}</p>
                <p>{{ $t('createNode.passwordTip4') }}</p>
                <p class="base-margin__left-20">{{ $t('createNode.lowercaseLetters') }}</p>
                <p class="base-margin__left-20">{{ $t('createNode.uppercaseLetters') }}</p>
                <p class="base-margin__left-20">{{ $t('createNode.numbers') }}</p>
                <p class="base-margin__left-20" v-text="'~!@#$%^&*-+=/'"></p>
              </div>
              <el-icon slot="reference" class="el-icon-question">hover {{ $t('createNode.activate') }}</el-icon>
            </el-popover>
          </span>
          <el-input v-model="password" class="base-width__240" show-password type="password" />
          <el-button class="base-margin__left-10" type="primary" @click="randomPassword">
            {{ $t('createNode.randomPassword') }}
          </el-button>
        </div>
        <div class="base-margin__bottom-20">
          <span class="base-margin__right-8">
            {{ $t('createNode.confirmPassword') }}
          </span>
          <el-input v-model="nextPassword" class="base-width__240" show-password type="password" />
        </div>
      </div>

      <!-- 密钥关联区域 -->
      <div v-else>
        <el-select
          v-model="describeKeyPairsData"
          :placeholder="$t('createNode.selectKey')"
          class="select-width"
          collapse-tags
          multiple
        >
          <el-option v-for="item in describeKeyPairsList" :key="item.KeyId" :label="item.KeyName" :value="item.KeyId" />
        </el-select>
      </div>
    </el-form-item>

    <!-- 超线程设置表单项 -->
    <el-form-item :label="$t('createNode.hyperThreading')" prop="hyperThreading">
      <el-checkbox v-model="thirdStepRuleForm.hyperThreading" @change="changeHyperThreading" :disabled="notTopology">
        <el-popover
          placement="bottom"
          title="提示"
          width="300"
          trigger="click"
          content="您已选择关闭超线程功能。此操作需要提前联系腾讯云客服申请白名单权限，否则实例将保持默认超线程设置"
          v-model="popoverVisible">
          <span slot="reference">
            {{ $t('createNode.disableHyperThreading') }}
          </span>
        </el-popover>
      </el-checkbox>
    </el-form-item>

    <!-- 节点价格显示表单项（不在价格黑名单的项目可见） -->
    <el-form-item
      v-if="!projectPriceBlacklist.includes(projectId.toString())"
      :label="$t('createNode.nodePrice')"
      class="price-color"
    >
      <div v-if="typeof thirdStepRuleForm.price === 'number' || thirdStepRuleForm.price === '-'">
        {{ thirdStepRuleForm.price }}
        <span v-if="thirdStepRuleForm.billingValue === 'PREPAID'">{{ $t('createNode.yuanPerMonth') }}</span>
        <span v-else>{{ $t('createNode.yuanPerHour') }}</span>
        <span v-if="thirdStepRuleForm.instanceNumber !== 1">
          <span class="base-margin__left-20"
            >( {{ $t('createNode.totalPrice') }}:
            {{ ((thirdStepRuleForm.instanceNumber * (thirdStepRuleForm.price * 100)) / 100).toFixed(2) }}
          </span>
          <span v-if="thirdStepRuleForm.billingValue === 'PREPAID'">{{ $t('createNode.yuanPerMonth') }} )</span>
          <span v-else>{{ $t('createNode.yuanPerHour') }} )</span>
        </span>
      </div>
      <i v-if="thirdStepRuleForm.price === 'loading'" class="el-icon-loading" />
    </el-form-item>
  </el-form>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { postRequest } from '@/common/libs/request';
import { defaultFilterRegion, regionList, workloadManagerList } from '@/common/utils/utils';
import { UTIL } from '@/common/utils';
import { Message } from 'element-ui';

export default {
  name: 'CreateNodeDialog',
  props: {
    secretId: {
      type: String,
      default: '',
    },
    secretKey: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    taskId: {
      // 项目ID
      type: Number,
      default: 0,
    },
    dialogDownloadVisible: {
      // 弹窗显示状态
      type: Boolean,
      default: false,
    },
    dialogType: {
      // 弹窗类型
      type: String,
      default: '',
    },
    closeDialog: Function, // 关闭弹窗方法
    isDoneDisabled: Function, // 禁用完成按钮方法
    closeLoading: Function, // 关闭加载状态方法
    getResourceList: Function, // 获取资源列表方法
  },
  data() {
    return {
      formLoading: false, // 表单加载状态
      isNotMaster: false, // 是否非主节点
      isCreateAdminNode: false, // 是否创建管理节点
      timeout: null, // 防抖计时器
      loading: false, // 加载状态
      form: {}, // 表单数据
      projectId: JSON.parse(sessionStorage.getItem('currentProject'))?.id || 0, // 当前项目ID
      userId: '', // 用户ID
      projectInstanceList: [], // 项目实例列表
      instanceTypeOption: [], // 实例类型选项
      instanceDescriptionOption: [], // 实例描述选项
      windowsType: false, // 是否为Windows类型
      masterEnvImageType: 'windows', // 主环境镜像类型
      thirdStepRuleForm: {
        model: '', // 模型
        auditor: '', // 审核人
        diskType: '', // 磁盘类型
        diskSize: '', // 磁盘大小
        instanceNumber: 1, // 实例数量
        billingValue: 'POSTPAID_BY_HOUR', // 计费方式
        regionValue: defaultFilterRegion(), // 区域值
        buyTime: 1, // 购买时长
        renew: 'NOTIFY_AND_AUTO_RENEW', // 续费方式
        price: '-', // 价格
        imageId: '', // 镜像ID
        workloadManager: 0, // 工作负载管理器
        nodeType: '', // 节点类型
        hyperThreading: false, // 超线程
        envId: null, // 环境ID
        zone: '', // 区域
        subnetId: '', // 子网ID
        queueName: '', // 队列名称
        imageType: 'SHARED_IMAGE', // 镜像平台
        imagePlatformType: '', // 镜像类型
      },
      passwordRadio: false, // 密码单选状态
      displayPasswordType: false, // 显示密码类型
      describeKeyPairsData: '', // 密钥对数据
      describeKeyPairsList: [], // 密钥对列表
      password: '', // 密码
      nextPassword: '', // 确认密码
      gibMinNumber: 0, // 最小磁盘大小
      notTopology: false, // 非拓扑状态
      popoverVisible: false, // 弹出框可见性

      // 计费方式数据
      billingTypeData: [
        { value: 'POSTPAID_BY_HOUR', text: this.$t('createNode.postpaidByHour') },
        { value: 'PREPAID', text: this.$t('createNode.prepaid') },
        { value: 'SPOTPAID', text: this.$t('createNode.spotpaid') },
      ],

      subnetList: [], // 子网列表
      zones: [], // 区域列表

      // 工作负载管理器列表
      workloadManagerList,

      regionData: regionList, // 区域数据

      // 购买时长选项
      buyTimeOptions: [
        { text: this.$t('createNode.oneMonth'), value: 1 },
        { text: this.$t('createNode.twoMonths'), value: 2 },
        // ...其他选项
        { text: this.$t('createNode.oneYear'), value: 12 },
        { text: this.$t('createNode.twoYears'), value: 24 },
        // ...其他选项
      ],

      diskList: [], // 磁盘列表

      // 节点类型列表
      nodeTypeList: [
        { text: this.$t('createNode.loginNode'), value: 'login', disabled: false },
        { text: this.$t('createNode.workerNode'), value: 'worker', disabled: false },
      ],

      imageOptionList: [], // 总的镜像选项
      imageOption: [], // 镜像选项

      // 续费选项
      renewOptions: [
        { text: this.$t('createNode.autoRenew'), value: 'NOTIFY_AND_AUTO_RENEW' },
        { text: this.$t('createNode.manualRenew'), value: 'NOTIFY_AND_MANUAL_RENEW' },
      ],

      // 磁盘选项
      diskOption: [
        { text: '50GB', value: 50, disabled: false },
        { text: '100GB', value: 100, disabled: false },
        { text: '150GB', value: 150, disabled: false },
      ],

      // 表单验证规则
      thirdStepRules: {
        model: [{ required: true, message: this.$t('createNode.inputContent'), trigger: 'change' }],
        auditor: [{ required: true, message: this.$t('createNode.inputContent'), trigger: 'change' }],
        diskSize: [{ required: true, message: this.$t('createNode.inputDiskSize'), trigger: 'change' }],
        imageId: [{ required: true, message: this.$t('createNode.selectImage'), trigger: 'change' }],
        billingValue: [{ required: true, message: this.$t('createNode.inputContent'), trigger: 'change' }],
        regionValue: [{ required: true, message: this.$t('createNode.inputRegion'), trigger: 'change' }],
        buyTime: [{ required: true, message: this.$t('createNode.inputContent'), trigger: 'change' }],
        renew: [{ required: true, message: this.$t('createNode.inputContent'), trigger: 'change' }],
        nodeType: [{ required: true, message: this.$t('createNode.selectNodeType'), trigger: 'change' }],
      },

      imageSize: 0, // 镜像大小
      showMore: false, // 显示更多
      currentProject: '', // 当前项目
      envList: [], // 环境列表
      queueList: [], // 队列列表
      projectPriceBlacklist: sessionStorage.getItem('projectPriceBlacklist')?.split(',') || [], // 项目价格黑名单
      recreateNode: '',
      imageTypeList: [], // 镜像类型列表
    };
  },
  computed: {
    // 是否为后付费
    isPostpaid() {
      return this.thirdStepRuleForm.billingValue === 'PREPAID';
    },
    ...mapState(['app']),
    ...mapGetters('app', ['getCurrentProject']),
  },
  watch: {
    // 监听当前项目变化
    'app.currentProject'(val) {
      const currentProject = this.getCurrentProject();
      if (currentProject.id !== this.projectId) {
        this.masterEnvImageType = currentProject.masterNode.imagePlatform;
        this.projectId = val.id;
        this.currentProject = currentProject;
      }
    },
  },
  // created() {
  //   this.currentProject = this.getCurrentProject();
  // },
  methods: {
    /**
     * 切换超线程
     */
    changeHyperThreading() {
      if (this.thirdStepRuleForm.hyperThreading) {
        this.popoverVisible = true;
      } else {
        this.popoverVisible = false;
      }
    },

    /**
     * 获取项目环境信息
     */
    async getProjectEnvInfo() {
      try {
        const res = await postRequest('/api/eda/common/project/getProjectEnvList', {
          offset: 0,
          limit: 999,
          region: this.thirdStepRuleForm.regionValue,
        });
        this.envList = res.list || [];
        if (this.thirdStepRuleForm.regionValue !== '') {
          await this.getInstanceList();
        } else {
          this.formLoading = false;
        }
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * 获取区域列表
     */
    getZones() {
      if (this.thirdStepRuleForm.regionValue === '') return;
      this.zones = [];
      postRequest('/api/eda/common/tencentcloud/describeZones', {
        region: this.thirdStepRuleForm.regionValue,
      }).then((res) => {
        this.zones = res.zoneSet;
      });
    },

    /**
     * 获取子网列表
     */
    getSubnet() {
      this.subnetList = [];
      let vpcId = '';
      if (this.thirdStepRuleForm.envId !== '') {
        vpcId = this.envList.filter(item => item.id === this.thirdStepRuleForm.envId)[0]?.vpcId || '';
      }
      if (this.thirdStepRuleForm.zone === '' || this.thirdStepRuleForm.envId === '' || vpcId === '') {
        return;
      }
      postRequest('/api/eda/common/tencentcloud/describeSubnetList', {
        region: this.thirdStepRuleForm.regionValue,
        vpcId,
        zone: this.thirdStepRuleForm.zone,
      }).then((res) => {
        this.subnetList = res.subnetSet;
      });
    },

    /**
     * 变更VPC区域
     */
    changeVpcZone() {
      this.thirdStepRuleForm.model = '';
      this.thirdStepRuleForm.auditor = '';
      this.thirdStepRuleForm.subnetId = '';
      this.subnetList = [];
      this.getInstanceList();
      this.getSubnet();
    },

    /**
     * 清空环境选择
     */
    clearEnv() {
      this.thirdStepRuleForm.envId = '';
      this.thirdStepRuleForm.zone = '';
      this.thirdStepRuleForm.subnetId = '';
    },

    /**
     * 选择环境ID
     */
    selectEnvId() {
      if (this.thirdStepRuleForm.envId === '') return;
      this.getSubnet();
    },

    /**
     * 变更磁盘大小
     */
    changeDiskSize() {
      this.getPrice();
    },

    /**
     * 变更磁盘类型
     */
    changeDisk() {
      this.getPrice();
    },

    /**
     * 获取磁盘类型
     * @param {String} instanceFamily 实例家族
     */
    getDisk(instanceFamily) {
      this.diskList = [];
      postRequest('/api/eda/common/tencentcloud/describeDiskType', {
        region: this.thirdStepRuleForm.regionValue,
        zone: this.thirdStepRuleForm.zone,
        cvmInstanceFamily: [instanceFamily],
      }).then((res) => {
        if (res.diskTypeSet == null || res.diskTypeSet.length === 0) {
          this.thirdStepRuleForm.diskType = '';
          this.thirdStepRuleForm.diskSize = '';
          return this.$message.error(this.$t('createNode.noAvailableDisk'));
        }
        this.diskList = res.diskTypeSet;
        const newArr = [];
        this.diskList.forEach((item) => {
          const newObj = {
            key: this.handleCbs(item.disk_type),
            value: item.disk_type,
            minSize: item.disk_size.min_size,
            remark: item.overall_performance || '',
          };
          newArr.push(newObj);
        });
        this.diskList = newArr;
        this.thirdStepRuleForm.diskType = this.diskList[0].value;
        this.thirdStepRuleForm.diskSize = this.imageSize;
        this.gibMinNumber = this.imageSize;
        // 获取价格
        if (typeof this.thirdStepRuleForm.price !== 'number') {
          this.getPrice();
        }
      });
    },

    /**
     * 处理磁盘类型显示
     * @param {String} diskType 磁盘类型
     * @returns {String} 显示文本
     */
    handleCbs(diskType) {
      switch (diskType) {
        case 'CLOUD_BASIC':
          return this.$t('createNode.cloudBasic');
        case 'CLOUD_PREMIUM':
          return this.$t('createNode.cloudPremium');
        case 'CLOUD_BSSD':
          return this.$t('createNode.cloudBssd');
        case 'CLOUD_SSD':
          return this.$t('createNode.cloudSsd');
        case 'CLOUD_HSSD':
          return this.$t('createNode.cloudHssd');
        case 'CLOUD_TSSD':
          return this.$t('createNode.cloudTssd');
        default:
          return '';
      }
    },

    /**
     * 防抖函数
     * @param {Function} cb 回调函数
     * @param {Number} timer 延迟时间
     */
    debounce(cb, timer) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(cb, timer);
    },

    /**
     * 创建节点
     * @returns {Promise} Promise对象
     */
    createNode(dryRun) {
      return new Promise((resolve) => {
        this.createProject(resolve, dryRun);
      });
    },

    /**
     * 创建项目
     * @param {Function} resolve Promise resolve函数
     */
    async createProject(resolve, dryRun) {
      if (this.windowsType) {
        // 密钥模式处理
        if (this.passwordRadio) {
          if (this.describeKeyPairsData.length === 0) {
            this.$message.warning(this.$t('createNode.selectKey'));
            this.loading = false;
            this.closeLoading();
            return false;
          }
        } else {
          // 密码模式处理
          // 验证密码格式
          if (!this.validPassword(this.password)) {
            return false;
          }
        }
      }

      this.$refs.thirdRuleForm.validate((valid) => {
        if (valid) {
          this.loading = true;
          const data = {
            region: this.thirdStepRuleForm.regionValue,
            instanceNumber: this.thirdStepRuleForm.instanceNumber,
            instanceType: this.thirdStepRuleForm.auditor,
            imageId: this.thirdStepRuleForm.imageId,
            instanceChargeType: this.thirdStepRuleForm.billingValue,
            renewFlag: this.thirdStepRuleForm.renew,
            period: this.thirdStepRuleForm.buyTime,
            diskType: this.thirdStepRuleForm.diskType,
            diskSize: this.thirdStepRuleForm.diskSize,
            nodeType: this.thirdStepRuleForm.nodeType,
            hyperThreading: !this.thirdStepRuleForm.hyperThreading,
            envId: this.thirdStepRuleForm.envId || 0,
            workloadManager: this.thirdStepRuleForm.workloadManager,
            subnetId: this.thirdStepRuleForm.subnetId,
            zone: this.thirdStepRuleForm.zone,
            queueName: this.thirdStepRuleForm.queueName,
            password: this.password === '' ? '' : UTIL.rsaEncryption(this.password),
            keypairIds: this.describeKeyPairsData.length === 0 ? [] : this.describeKeyPairsData,
            imageType: this.thirdStepRuleForm.imageType,
          };

          if (this.isCreateAdminNode) {
            // 发送创建主节点请求
            postRequest('/api/eda/common/project/createMasterNode', data)
              .then(() => {
                this.$message.success(this.$t('createNode.createNodeSuccess', {
                  time: this.windowsType ? '10~15' : '3~5',
                }));
                this.closeForm();
                resolve();
              })
              .catch(() => {
                this.loading = false;
                this.closeLoading();
              });
          } else {
            // 子节点类型
            data.nodeType = this.thirdStepRuleForm.nodeType;
            // 验证参数
            data.dryRun = dryRun;
            // 创建子节点
            postRequest('/api/eda/common/cluster/createClusterComputeNodes', data)
              .then(() => {
                if (dryRun) {
                  this.closeLoading();
                  Message.success(this.$t('customCluster.addInstanceDialog.canCreate'));
                } else {
                  this.$message.success(this.$t('createNode.createNodeSuccess', {
                    time: this.windowsType ? '10~15' : '3~5',
                  }));
                  this.getResourceList();
                  this.closeForm();
                  resolve();
                }
              })
              .catch(() => {
                this.loading = false;
                this.closeLoading();
              });
          }
        } else {
          this.closeLoading();
        }
      });
    },

    /**
     * 关闭表单
     */
    closeForm() {
      // 清除表单数据
      this.thirdStepRuleForm = {
        model: '',
        auditor: '',
        billingValue: 'POSTPAID_BY_HOUR',
        regionValue: '',
        buyTime: 1,
        renew: 'NOTIFY_AND_AUTO_RENEW',
        price: '-',
        zone: '',
        imageId: '',
        instanceNumber: 1,
        workloadManager: 0,
        hyperThreading: false,
        envId: '',
        diskType: '',
        diskSize: '',
        queueName: '',
      };
      this.password = '';
      this.nextPassword = '';
      this.describeKeyPairsData = [];
      this.notTopology = false;
      this.imageSize = 0;
      this.loading = false;
      this.closeDialog();
      this.$refs.thirdRuleForm.clearValidate();
    },

    /**
     * 获取价格
     */
    getPrice() {
      this.thirdStepRuleForm.price = 'loading';
      const data = {
        region: this.thirdStepRuleForm.regionValue,
        zone: this.thirdStepRuleForm.zone,
        imageId: this.thirdStepRuleForm.imageId,
        instanceChargeType: this.thirdStepRuleForm.billingValue,
        period: this.thirdStepRuleForm.buyTime || 1,
        instanceType: this.thirdStepRuleForm.auditor,
        renewFlag: this.thirdStepRuleForm.renew,
        nodeType: this.thirdStepRuleForm.nodeType,
        diskType: this.thirdStepRuleForm.diskType,
        diskSize: this.thirdStepRuleForm.diskSize,
      };

      // 重新创建需要projectId
      if (this.dialogType === 'createNode') {
        Object.assign(data, {
          type: 'reCreate', // 重新创建
        });
      }

      // 判断是否有必填但是未填的参数
      if (
        !data.diskSize
        || !data.instanceType
        || !data.imageId
        || !data.region
        || !data.diskType
        || data.workloadManager === ''
      ) {
        this.thirdStepRuleForm.price = '-';
        return false;
      }

      // 发送获取价格请求
      postRequest('/api/eda/common/tencentcloud/inquiryPriceRunInstances', data)
        .then((res) => {
          const temp
            = res.unitPriceDiscountPrice * 0.01
            || res.instanceDiscountPrice * 0.01
            || res.instanceUnitPrice * 0.01
            || res.instanceOriginalPrice * 0.01;

          if (typeof temp === 'number') {
            this.isDoneDisabled(false);
            this.thirdStepRuleForm.price = +temp.toFixed(2);
          }
        })
        .catch(() => {
          this.thirdStepRuleForm.price = '-';
        });
    },

    /**
     * 选择计费方式
     * @returns {Map} 计费方式映射
     */
    selectBilling() {
      let cvmsDataMap = new Map();
      if (this.thirdStepRuleForm.regionValue === '') return;
      if (this.thirdStepRuleForm.billingValue === 'POSTPAID_BY_HOUR') {
        [cvmsDataMap] = this.allCvmsData;
      }
      if (this.thirdStepRuleForm.billingValue === 'PREPAID') {
        [, cvmsDataMap] = this.allCvmsData;
      }
      if (this.thirdStepRuleForm.billingValue === 'SPOTPAID') {
        [, , cvmsDataMap] = this.allCvmsData;
      }
      return cvmsDataMap;
    },

    /**
     * 选择处理器
     * @param {*} value 选择的值
     * @param {String} type 选择类型
     */
    async selectHandler(value, type) {
      this.isDoneDisabled(true);

      // 选择地区
      if (type === 'region') {
        this.instanceTypeOption = [];
        this.instanceDescriptionOption = [];
        this.thirdStepRuleForm.regionValue = value;
        this.formLoading = true;
        this.thirdStepRuleForm.imageId = '';
        this.thirdStepRuleForm.model = '';
        this.thirdStepRuleForm.auditor = '';
        this.thirdStepRuleForm.price = '-';
        this.thirdStepRuleForm.diskSize = '';
        this.thirdStepRuleForm.diskType = '';
        this.thirdStepRuleForm.envId = '';
        this.thirdStepRuleForm.zone = '';
        this.thirdStepRuleForm.subnetId = '';
        this.thirdStepRuleForm.imageType = 'SHARED_IMAGE';
        this.imageOption = [];
        this.imageOptionList = [];
        this.notTopology = false;
        await this.getImageList();
        await this.getProjectEnvInfo();
        await this.getZones();
        await this.getSubnet();
      }

      const cvmsDataMap = this.selectBilling();

      if (type === 'billingValue') {
        this.thirdStepRuleForm.model = null;
        this.thirdStepRuleForm.auditor = '';
        this.thirdStepRuleForm.price = '-';
        this.selectInstanceChargeType(this.thirdStepRuleForm.billingValue);
      }

      // 服务器机型选择
      if (type === 'type') {
        const instanceDescriptionList = [];
        if (value) {
          if (this.thirdStepRuleForm.nodeType === 'worker') {
            this.thirdStepRuleForm.queueName = value.indexOf('GPU') !== -1 ? 'gpuq' : 'cpuq';
          }

          // 构造服务器配置选项
          for (const cvm of cvmsDataMap.get(value)) {
            let description = `${cvm.Cpu}${this.$t('createNode.core')}${cvm.Memory}GB ${cvm.Remark} | ${
              cvm.InstanceType
            }`;
            let disabled = false;
            if (cvm.Status === 'SOLD_OUT') {
              description = `${description}(${this.$t('createNode.soldOut')})`;
              disabled = true;
            }

            const descriptionOption = {
              value: cvm.InstanceType,
              text: description,
              disabled,
              instanceFamily: cvm.InstanceFamily,
              vCpu: cvm.Cpu,
              memory: cvm.Memory,
              frequency: cvm.Frequency,
              cpuType: cvm.CpuType,
              instanceBandwidth: cvm.InstanceBandwidth,
              networkCard: cvm.NetworkCard,
              instancePps: cvm.InstancePps,
            };
            instanceDescriptionList.push(descriptionOption);
          }
        }

        this.getDisk(instanceDescriptionList[0]?.instanceFamily || '');
        this.thirdStepRuleForm.auditor = '';
        this.thirdStepRuleForm.model = value;
        this.thirdStepRuleForm.price = '-';
        this.instanceDescriptionOption = instanceDescriptionList;
      }

      // 服务器配置选择
      if (type === 'description' && value) {
        const cvmsDataList = cvmsDataMap.get(this.thirdStepRuleForm.model);
        let cvmInfo;
        if (cvmsDataList) {
          cvmsDataList.forEach((cvmData) => {
            if (cvmData.InstanceType === value) {
              cvmInfo = cvmData;
            }
          });
        }

        if (cvmInfo) {
          this.notTopology = cvmInfo.Cpu < 2;
          this.thirdStepRuleForm.hyperThreading = this.notTopology;
          this.thirdStepRuleForm.auditor = value;
          this.thirdStepRuleForm.zone = cvmInfo.Zone;
        }
      }

      // 镜像和磁盘大小选择
      if (type === 'imageIdAndDiskSize') {
        this.imageOption.forEach((item) => {
          if (item.value === value.imageId) {
            this.thirdStepRuleForm.imageId = value.imageId;
            this.windowsType = item.text.indexOf('windows') === -1;
            this.nodeTypeList[1].disabled = !this.windowsType;

            if (this.deployMode) {
              this.thirdStepRuleForm.nodeType = 'login';
              this.nodeTypeList[1].disabled = true;
            }
            if (item.size > this.thirdStepRuleForm.diskSize) {
              this.thirdStepRuleForm.diskSize = item.size;
            }
          }
        });
        this.imageSize = value.diskSize;
      }

      // 镜像选择
      if (type === 'imageId') {
        this.thirdStepRuleForm.diskSize = '';
        this.thirdStepRuleForm.diskType = '';
        this.imageOption.forEach((item) => {
          if (item.value === value) {
            this.thirdStepRuleForm.imageId = value;
            this.windowsType = item.text.indexOf('windows') === -1;
            this.nodeTypeList[1].disabled = !this.windowsType;

            if (this.deployMode) {
              this.thirdStepRuleForm.nodeType = 'login';
              this.nodeTypeList[1].disabled = true;
            }
            this.thirdStepRuleForm.diskSize = item.size;
            this.imageSize = item.size;
            this.gibMinNumber = item.size;
          }
        });
      }

      // 节点类型选择
      if (type === 'nodeType') {
        if (value === 'login') {
          this.thirdStepRuleForm.queueName = 'login';
          return;
        }

        if (this.thirdStepRuleForm.model === '') {
          this.thirdStepRuleForm.queueName = 'cpuq';
          return;
        }

        this.thirdStepRuleForm.queueName = this.thirdStepRuleForm.model.indexOf('GPU') !== -1 ? 'gpuq' : 'cpuq';
      }

      // 判断是否有必填但是未填的参数
      if (
        this.thirdStepRuleForm.diskSize === ''
        || this.thirdStepRuleForm.model === ''
        || this.thirdStepRuleForm.imageId === ''
        || this.thirdStepRuleForm.regionValue === ''
        || this.thirdStepRuleForm.diskType === ''
        || this.thirdStepRuleForm.workloadManager === ''
      ) {
        this.thirdStepRuleForm.price = '-';
      } else {
        if (type !== 'descption' || type !== 'type') {
          this.getPrice();
        }
      }
    },

    /**
     * 创建管理节点
     * @param {*} data 数据
     * @param {*} env 环境
     */
    async createAdminNode(data, env) {
      this.recreateNode = data;
      switch (data) {
        case 'newNode':
          this.billingTypeData = [
            { value: 'POSTPAID_BY_HOUR', text: this.$t('createNode.postpaidByHour') },
            { value: 'PREPAID', text: this.$t('createNode.prepaid') },
            { value: 'SPOTPAID', text: this.$t('createNode.spotpaid') },
          ];
          await this.createNewNode(data, env);
          await this.getCommonData();
          this.thirdStepRuleForm.queueName = 'login';
          if (env.deployModeSimple) {
            this.deployMode = env.deployModeSimple;
            this.thirdStepRuleForm.nodeType = 'login';
            this.nodeTypeList[1].disabled = true;
          }
          break;
        case 'newMasterNode':
          this.billingTypeData = [
            { value: 'POSTPAID_BY_HOUR', text: this.$t('createNode.postpaidByHour') },
            { value: 'PREPAID', text: this.$t('createNode.prepaid') },
          ];
          this.thirdStepRuleForm.queueName = '';
          this.isCreateAdminNode = true;
          await this.createNewNode(data);
          await this.getCommonData();
          break;
        default:
          this.recreateNode = 'recreateMasterNode';
          this.billingTypeData = [
            { value: 'POSTPAID_BY_HOUR', text: this.$t('createNode.postpaidByHour') },
            { value: 'PREPAID', text: this.$t('createNode.prepaid') },
          ];
          this.thirdStepRuleForm.regionValue = data.region || defaultFilterRegion();
          this.thirdStepRuleForm.zone = data.zone;
          this.thirdStepRuleForm.hyperThreading = !data.hyperThreading;
          this.thirdStepRuleForm.billingValue = data.instanceChargeType;
          this.thirdStepRuleForm.workloadManager = data.workloadManager;
          this.thirdStepRuleForm.diskSize = data.diskSize;
          this.thirdStepRuleForm.diskType = data.diskType;
          this.thirdStepRuleForm.envId = env?.env?.id || '';
          this.thirdStepRuleForm.subnetId = env?.env?.subnetId || '';
          this.thirdStepRuleForm.queueName = data.queueName || '';
          this.thirdStepRuleForm.imageType = data.imageType || 'SHARED_IMAGE';
          this.thirdStepRuleForm.imageId = data.imageId || '';
          this.formLoading = true;
          this.isCreateAdminNode = true;
          await this.getImageList();
          await this.getProjectEnvInfo();
          await this.awaitShowData(data);
          await this.selectHandler(data, 'imageIdAndDiskSize');
          await this.getZones();
          await this.getCommonData();
          await this.getSubnet();
          break;
      }
    },

    /**
     * 等待显示数据
     * @param {*} data 数据
     */
    awaitShowData(data) {
      const cvmsDataMap = this.selectBilling();
      if (!cvmsDataMap) return;
      for (const cvm of cvmsDataMap.values()) {
        cvm.forEach((item) => {
          if (data.instanceType === item.InstanceType) {
            this.selectHandler(item.TypeName, 'type');
            this.selectHandler(item.InstanceType, 'description');
            this.thirdStepRuleForm.zone = item.Zone;
          }
        });
      }
    },

    /**
     * 获取通用数据
     */
    getCommonData() {
      // 获取项目全部队列列表
      postRequest('/api/eda/common/cluster/describeQueueList').then((res) => {
        this.queueList = res?.queueList.map(item => ({ value: item }));
      });

      // 获取密钥对列表
      postRequest('/api/eda/common/tencentcloud/describeKeyPairs', {
        limit: 100,
        offset: 0,
      }).then((res) => {
        this.describeKeyPairsList = res?.list || [];
      });
    },

    /**
     * 自动完成查询搜索
     * @param {String} queryString 查询字符串
     * @param {Function} cb 回调函数
     */
    autocompleteQuerySearch(queryString, cb) {
      const restaurants = this.queueList;
      const results = queryString ? restaurants.filter(this.createFilter(queryString)) : restaurants;
      cb(results);
    },

    /**
     * 创建过滤器
     * @param {String} queryString 查询字符串
     * @returns {Function} 过滤函数
     */
    createFilter(queryString) {
      return restaurant => restaurant.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0;
    },

    /**
     * 获取实例列表
     * @param {Array} res 响应数据
     */
    getInstance(res) {
      const cvmMapPostpaidByHour = new Map();
      const cvmMapPrepaid = new Map();
      const cvmMapBidding = new Map();

      // 根据机型去重
      const cvmNonredundantPostpaidByHourMap = new Map();
      const cvmNonredundantPrepaidMap = new Map();
      const cvmBiddingInstanceMap = new Map();

      res.forEach((cvm) => {
        if (cvm.InstanceChargeType === 'POSTPAID_BY_HOUR') {
          cvmNonredundantPostpaidByHourMap.set(cvm.InstanceType, cvm);
        }
        if (cvm.InstanceChargeType === 'PREPAID') {
          cvmNonredundantPrepaidMap.set(cvm.InstanceType, cvm);
        }
        if (cvm.InstanceChargeType === 'SPOTPAID') {
          cvmBiddingInstanceMap.set(cvm.InstanceType, cvm);
        }
      });

      // 构造按量计费结构
      for (const cvm of cvmNonredundantPostpaidByHourMap.values()) {
        if (cvmMapPostpaidByHour.has(cvm.TypeName)) {
          cvmMapPostpaidByHour.get(cvm.TypeName).push(cvm);
        } else {
          cvmMapPostpaidByHour.set(cvm.TypeName, [cvm]);
        }
      }

      // 构造包年包月结构
      for (const cvm of cvmNonredundantPrepaidMap.values()) {
        if (cvmMapPrepaid.has(cvm.TypeName)) {
          cvmMapPrepaid.get(cvm.TypeName).push(cvm);
        } else {
          cvmMapPrepaid.set(cvm.TypeName, [cvm]);
        }
      }

      // 构造竞价实例结构
      for (const cvm of cvmBiddingInstanceMap.values()) {
        if (cvmMapBidding.has(cvm.TypeName)) {
          cvmMapBidding.get(cvm.TypeName).push(cvm);
        } else {
          cvmMapBidding.set(cvm.TypeName, [cvm]);
        }
      }

      this.allCvmsData = [cvmMapPostpaidByHour, cvmMapPrepaid, cvmMapBidding];
      this.selectInstanceChargeType(this.thirdStepRuleForm.billingValue);
    },

    /**
     * 选择实例计费类型
     * @param {String} type 计费类型
     */
    selectInstanceChargeType(type) {
      const instanceTypeList = [];
      let cvmsDataMap;

      if (type === 'POSTPAID_BY_HOUR') {
        [cvmsDataMap] = this.allCvmsData;
      }
      if (type === 'PREPAID') {
        [, cvmsDataMap] = this.allCvmsData;
      }
      if (type === 'SPOTPAID') {
        [, , cvmsDataMap] = this.allCvmsData;
      }

      // 构造服务器机型选项
      if (cvmsDataMap) {
        for (const key of cvmsDataMap.keys()) {
          const typeOption = {
            value: key,
            text: key,
          };
          instanceTypeList.push(typeOption);
        }
      }

      this.instanceTypeOption = instanceTypeList;
      this.formLoading = false;
    },

    /**
     * 选择镜像类型
     */
    changeImageType(type) {
      this.thirdStepRuleForm.imageId = '';
      if (type === 'imageType') {
        this.thirdStepRuleForm.imagePlatformType = '';
        this.getImageList();
      } else {
        if (this.thirdStepRuleForm.imagePlatformType === '') {
          this.imageOption = this.imageOptionList;
        } else {
          this.imageOption = this.imageOptionList.filter(item => item.platform === this.thirdStepRuleForm.imagePlatformType);
        }
      }
    },

    /**
     * 获取镜像列表
     */
    getImageList() {
      const param = {
        region: this.thirdStepRuleForm.regionValue,
        tags: ['openeda:'],
        imageType: this.thirdStepRuleForm.imageType,
        platformList: '',
      };

      if (this.getCurrentProject()?.masterNode?.imagePlatform === '') {
        param.platformList = [];
      } else {
        if (this.recreateNode === 'recreateMasterNode') {
          param.platformList = [];
        } else {
          param.platformList
            = this.getCurrentProject()?.masterNode?.imagePlatform === 'windows'
              ? ['Windows']
              : ['CentOS', 'Ubuntu', 'TencentOS'];
        }
      }

      this.thirdStepRuleForm.imagePlatformType = '';
      // 当地区为空时，不进行请求
      if (param.region === '') return;

      return postRequest('/api/eda/common/eda/getImageList', param).then((res) => {
        this.imageOptionList = [];
        this.imageOption = [];
        const platformSet = new Set();
        platformSet.add(''); // 添加"全部"选项
        res?.list.forEach((item) => {
          // 当创建管理节点时，展示全部镜像
          // 当创建子节点时则根据管理节点的镜像类型进行过滤
          this.imageOption.push({
            text: item.name,
            value: item.value,
            size: item.disk,
            platform: item.platform,
            osName: item.osName,
          });
          platformSet.add(item.platform);
        });
        this.imageOptionList = this.imageOption;
        this.imageTypeList = Array.from(platformSet).map(platform => ({
          text: platform === '' ? this.$t('common.all') : platform,
          value: platform,
        }));
      });
    },

    /**
     * 获取实例列表
     */
    async getInstanceList() {
      if (this.thirdStepRuleForm.regionValue === '') return;
      const param = {
        region: this.thirdStepRuleForm.regionValue,
        zone: this.thirdStepRuleForm.zone,
      };

      const res = await postRequest('/api/eda/common/eda/getInstanceList', param);
      this.getInstance(res?.InstanceList || []);
    },

    /**
     * 检查密码单选
     * @param {Boolean} val 值
     */
    checkPasswordRadio(val) {
      if (val === true) {
        this.displayPasswordType = true;
        this.password = '';
        this.nextPassword = '';
      }
      if (val === false) {
        this.describeKeyPairsData = [];
        this.displayPasswordType = false;
      }
    },

    /**
     * 验证密码
     * @param {String} pwd 密码
     * @returns {Boolean} 是否有效
     */
    validPassword(pwd) {
      if (pwd.length < 12 || pwd.length > 30) {
        this.$message.warning(this.$t('createNode.passwordLengthError'));
        this.loading = false;
        this.closeLoading();
        return false;
      }
      if (pwd.includes(' ')) {
        this.$message.warning(this.$t('createNode.passwordNoSpace'));
        this.loading = false;
        this.closeLoading();
        return false;
      }
      if (this.password !== this.nextPassword) {
        Message.warning(this.$t('createNode.passwordNotMatch'));
        this.loading = false;
        this.closeLoading();
        return false;
      }

      if (pwd.startsWith('/')) {
        this.$message.warning(this.$t('createNode.passwordNoSlash'));
        this.loading = false;
        this.closeLoading();
        return false;
      }

      // 密码必须包含至少三种字符类型
      let hasLower = 0;
      let hasUpper = 0;
      let hasDigit = 0;
      let hasSpecial = 0;

      for (let i = 0; i < pwd.length; i++) {
        const char = pwd[i];
        if (char >= 'a' && char <= 'z') {
          hasLower = 1;
        } else if (char >= 'A' && char <= 'Z') {
          hasUpper = 1;
        } else if (char >= '0' && char <= '9') {
          hasDigit = 1;
        } else if ('()`~!@#$%^&*-+=_|{}[]:;\\\'<>,.?/'.includes(char)) {
          hasSpecial = 1;
        } else {
          this.$message.warning(this.$t('createNode.passwordInvalidChars'));
          this.loading = false;
          this.closeLoading();
          return false;
        }
      }

      if (hasLower + hasUpper + hasDigit + hasSpecial < 3) {
        this.$message.warning(this.$t('createNode.passwordRequirement'));
        this.loading = false;
        this.closeLoading();
        return false;
      }

      return true;
    },

    /**
     * 生成随机密码
     */
    randomPassword() {
      const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
      const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const numbers = '0123456789';
      const specialChars = '~!@#$%^&*-+=/';
      const allChars = lowerCase + upperCase + numbers + specialChars;
      const passwordLength = 12;
      let password = '';
      const charTypes = [false, false, false, false]; // [小写, 大写, 数字, 特殊字符]

      // 确保至少包含三种字符类型
      const ensureTypes = () => {
        const typeIndex = Math.floor(Math.random() * 4);
        switch (typeIndex) {
          case 0:
            password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
            charTypes[0] = true;
            break;
          case 1:
            password += upperCase[Math.floor(Math.random() * upperCase.length)];
            charTypes[1] = true;
            break;
          case 2:
            password += numbers[Math.floor(Math.random() * numbers.length)];
            charTypes[2] = true;
            break;
          case 3:
            password += specialChars[Math.floor(Math.random() * specialChars.length)];
            charTypes[3] = true;
            break;
        }
      };

      // 确保至少包含三种字符类型
      for (let i = 0; i < 3; i++) {
        ensureTypes();
      }

      // 生成剩余的密码字符
      for (let i = password.length; i < passwordLength; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }

      // 确保密码不以 '/' 开头
      while (password[0] === '/') {
        password = password.slice(1);
      }

      // 打乱密码字符顺序
      password = password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('');

      this.password = `t${password}`;
      this.nextPassword = `t${password}`;

      if (this.thirdStepRuleForm.price === '-') {
        this.getPrice();
      }
    },

    /**
     * 创建新节点
     */
    async createNewNode(type, data) {
      try {
        // 获取项目信息
        const currentProject = this.getCurrentProject() || {};
        this.currentProject = currentProject;
        this.projectId = currentProject.id;
        this.masterEnvImageType = currentProject?.masterNode?.imagePlatform || '';
        this.thirdStepRuleForm.diskType = currentProject?.env?.diskType || '';
        this.thirdStepRuleForm.diskSize = currentProject?.env?.diskSize || '';
        this.thirdStepRuleForm.workloadManager = currentProject?.env?.workloadManager || 0;
        this.thirdStepRuleForm.envId = currentProject?.env?.id || '';
        this.thirdStepRuleForm.queueName = currentProject?.env?.queueName || '';
        this.thirdStepRuleForm.imageType = 'SHARED_IMAGE';
        this.formLoading = true;

        if (type === 'newNode') {
          this.thirdStepRuleForm.regionValue = data.masterRegion;
        } else {
          this.thirdStepRuleForm.regionValue = currentProject?.env?.region || defaultFilterRegion();
        }

        if (this.thirdStepRuleForm.regionValue === '') {
          this.formLoading = false;
          return;
        }

        this.$nextTick(async () => {
          await this.getProjectEnvInfo();
          await this.getImageList();
          await this.getZones();
          await this.getSubnet();
        });
      } catch (e) {
        this.formLoading = false;
        console.error(e);
      }
    },
  },
};
</script>

<style>
.el-select {
  width: 100%;
}

.select-width {
  width: 100%;
}

.select-width_90 {
  width: 90%;
}

.form__item-margin {
  margin-bottom: 0 !important;
}

.price-color {
  color: #e97e00;
}

.formItem__select-short {
  width: 200px;
}
</style>
